/**
 * FindingsRegistry — registers rules, runs them over a context, and
 * assembles a `FindingsReport`. Order-independent; the registry sorts
 * the final flat list by severity then rule id.
 *
 * Rules register **once** at module load via `registerFindingRule`.
 * Prefer one file per rule under `./rules/`; barrel `index.ts`
 * triggers registration through an import-for-side-effect.
 */

import type {
  ExplorerFinding,
  FindingsReport,
  FindingSeverity,
} from '../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from './types';

const SEVERITY_ORDER: Readonly<Record<FindingSeverity, number>> = {
  critical: 0,
  warn: 1,
  info: 2,
};

class FindingsRegistryImpl {
  private readonly rules = new Map<string, FindingRule>();

  register(rule: FindingRule): void {
    if (this.rules.has(rule.id)) {
      // Overwriting is intentional in dev (HMR); keep latest silently.
      this.rules.delete(rule.id);
    }
    this.rules.set(rule.id, rule);
  }

  unregister(id: string): void {
    this.rules.delete(id);
  }

  list(): readonly FindingRule[] {
    return Array.from(this.rules.values());
  }

  run(ctx: FindingRuleContext): FindingsReport {
    const all: ExplorerFinding[] = [];
    for (const rule of this.rules.values()) {
      try {
        const produced = rule.run(ctx);
        for (const f of produced) all.push(f);
      } catch (err) {
        // A buggy rule must not break the whole report.
        // Upstream callers pipe through the backend Logger; in browser
        // context we surface via console.warn.
        if (typeof console !== 'undefined') {
          console.warn(`[FindingsRegistry] rule '${rule.id}' failed`, err);
        }
      }
    }

    all.sort((a, b) => {
      const sa = SEVERITY_ORDER[a.severity];
      const sb = SEVERITY_ORDER[b.severity];
      if (sa !== sb) return sa - sb;
      return a.ruleId.localeCompare(b.ruleId);
    });

    const byBubble = new Map<string, ExplorerFinding[]>();
    const global: ExplorerFinding[] = [];
    for (const f of all) {
      if (f.scope === 'bubble' && f.subject?.kind === 'bubble') {
        const arr = byBubble.get(f.subject.id) ?? [];
        arr.push(f);
        byBubble.set(f.subject.id, arr);
      } else if (f.scope === 'global') {
        global.push(f);
      }
    }

    return { all, byBubble, global };
  }
}

export const FindingsRegistry = new FindingsRegistryImpl();

export function registerFindingRule(rule: FindingRule): void {
  FindingsRegistry.register(rule);
}
